// Castintech dashboard — Castdesign Design Jobs view modülü.
// Aşama I4 (2026-05-29) — Castdesign SDK köprüsü (Aşama I1) ile açılan
// design job'larını listeler (queued/running/completed/failed).
//
// İçerik:
//   - Tablo: tenant/client · mode · output_type · status (renkli rozet) ·
//     owner_agent · brief (kısalt) · created_at
//   - Status filtre dropdown (queued/running/completed/failed)
//   - GET /castintech-api/castdesign/jobs (requireAuth) — max 100, created_at DESC
//   - state-display (D5) skeleton/empty/error + microfx scrollReveal (D2)
//
// CSS: holding-views.css `.sat-*` sınıfları reuse (yeni CSS dosyası yok).

import { apiFetch } from '../api.js';
import { state } from '../state.js';
import { escHtml, $ } from '../utils.js';
import { createSkeleton } from '../ui/state-display.js';

interface DesignJob {
  id: string;
  tenant: string;
  client_slug: string | null;
  castdesign_project_id: string | null;
  castdesign_job_id: string | null;
  mode: string | null;
  output_type: string | null;
  status: string;
  brief: string | null;
  requested_by: string | null;
  owner_agent: string | null;
  bundle_path: string | null;
  created_at: string;
  updated_at: string;
}

let _jobs: DesignJob[] = [];
let _error: string | null = null;
let _loaded = false;
let _statusFilter = '';

// Gem palette (cockpit-tokens ile aynı hex; mor gradient yok).
const STATUS_META: Record<string, { label: string; color: string }> = {
  queued: { label: 'Sırada', color: '#fbbf24' },        // amber
  running: { label: 'Çalışıyor', color: '#60a5fa' },    // sapphire
  completed: { label: 'Tamamlandı', color: '#34d399' }, // emerald
  failed: { label: 'Başarısız', color: '#f87171' },     // ruby
};

const STATUS_ORDER = ['queued', 'running', 'completed', 'failed'];

export async function loadDesignJobs(): Promise<void> {
  if (!_loaded && state.currentPage === 'design-jobs') {
    const root = $('design-jobs-content');
    if (root && !root.querySelector('.sat-layout')) {
      root.innerHTML = '';
      root.appendChild(createSkeleton({ rows: 6, height: '44px', rowGap: '8px' }));
    }
  }
  try {
    const qs = _statusFilter ? `?status=${encodeURIComponent(_statusFilter)}` : '';
    const r = await apiFetch('/castintech-api/castdesign/jobs' + qs);
    if (!r.ok) throw new Error('castdesign/jobs HTTP ' + r.status);
    const d = await r.json() as { design_jobs?: DesignJob[] };
    _jobs = d.design_jobs || [];
    _error = null;
  } catch (e) {
    _error = (e as Error).message || 'Design job verisi yüklenemedi';
    console.warn('loadDesignJobs failed:', _error);
  } finally {
    _loaded = true;
  }
  if (state.currentPage === 'design-jobs') renderDesignJobs();
}

function statusBadge(status: string): string {
  const meta = STATUS_META[status] || { label: status, color: 'var(--muted)' };
  return `<span style="font-size:10px;font-weight:700;padding:2px 8px;border-radius:10px;color:${meta.color};background:${meta.color}22;">${escHtml(meta.label)}</span>`;
}

function shortBrief(b: string | null): string {
  if (!b) return '<span style="color:var(--muted)">—</span>';
  const t = b.length > 64 ? b.slice(0, 64) + '…' : b;
  return `<span title="${escHtml(b)}">${escHtml(t)}</span>`;
}

export function renderDesignJobs(): void {
  const root = $('design-jobs-content');
  if (!root) return;
  const sub = $('design-jobs-sub');
  if (sub) sub.textContent = `Castdesign SDK köprüsü · ${_jobs.length} job (max 100)`;

  if (_error) {
    root.innerHTML = `<div class="ds-error" role="alert" style="margin:8px 0">
      <div class="ds-error-icon" aria-hidden="true">⚠️</div>
      <div class="ds-error-title">Design job verisi yüklenemedi</div>
      <div class="ds-error-desc">${escHtml(_error)}</div>
      <button class="ds-error-retry" type="button" id="dj-retry-btn">Tekrar dene</button>
    </div>`;
    const btn = root.querySelector<HTMLButtonElement>('#dj-retry-btn');
    if (btn) btn.addEventListener('click', () => { void loadDesignJobs(); });
    return;
  }

  const filterHtml = `
    <div style="display:flex;align-items:flex-end;gap:8px;">
      <label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">Durum
        <select id="dj-status-filter" class="form-select" style="width:150px;padding:4px 8px;">
          <option value=""${_statusFilter === '' ? ' selected' : ''}>Tümü</option>
          ${STATUS_ORDER.map(s => `<option value="${s}"${_statusFilter === s ? ' selected' : ''}>${escHtml(STATUS_META[s].label)}</option>`).join('')}
        </select>
      </label>
    </div>`;

  let bodyHtml: string;
  if (_jobs.length === 0) {
    bodyHtml = `<div class="ds-empty" role="status" style="margin:8px 0">
      <div class="ds-empty-icon" aria-hidden="true">🎨</div>
      <div class="ds-empty-title">Henüz design job yok.</div>
      <div class="ds-empty-desc">Castdesign köprüsü (Aşama I1) bir job açtığında burada listelenir.</div>
    </div>`;
  } else {
    bodyHtml = `<div class="sat-section">
      <table class="sat-table">
        <thead><tr>
          <th>Müşteri</th><th>Mod</th><th>Çıktı</th><th>Durum</th>
          <th>Sahip Ajan</th><th>Brief</th><th>Oluşturuldu</th>
        </tr></thead>
        <tbody>${_jobs.map(j => `
          <tr data-reveal>
            <td>${escHtml(j.client_slug || j.tenant || '—')}${j.client_slug && j.tenant ? ` <span class="sat-proj">/${escHtml(j.tenant)}</span>` : ''}</td>
            <td>${escHtml(j.mode || '—')}</td>
            <td>${escHtml(j.output_type || '—')}</td>
            <td>${statusBadge(j.status)}</td>
            <td>${escHtml(j.owner_agent || '—')}</td>
            <td>${shortBrief(j.brief)}</td>
            <td class="sat-date">${escHtml((j.created_at || '').slice(0, 16).replace('T', ' '))}</td>
          </tr>`).join('')}</tbody>
      </table>
      <div style="margin-top:8px;font-size:11px;color:var(--muted)">
        Durum: <span style="color:#fbbf24">●</span> Sırada · <span style="color:#60a5fa">●</span> Çalışıyor ·
        <span style="color:#34d399">●</span> Tamamlandı · <span style="color:#f87171">●</span> Başarısız
        · created_at DESC · GET /castdesign/jobs
      </div>
    </div>`;
  }

  root.innerHTML = `<div class="sat-layout">${filterHtml}${bodyHtml}</div>`;

  const sel = root.querySelector<HTMLSelectElement>('#dj-status-filter');
  if (sel) {
    sel.addEventListener('change', () => {
      _statusFilter = sel.value;
      void loadDesignJobs();
    });
  }

  // Aşama D2 — scrollReveal (prefers-reduced-motion saygısı CSS @media globalde).
  if (_jobs.length > 0) {
    import('../ui/microfx.js').then((mfx) => {
      mfx.scrollReveal('#design-jobs-content [data-reveal]');
    }).catch(() => { /* microfx yoksa sessiz geç */ });
  }
}
